/**
 * Transform utility for CPSC citation codes.
 *
 * Schema format: { code: string, description: string }[]
 * Form format (labelInValue): { value: string, label: string, title?: string }[]
 *
 * Label format: "code - description" (e.g., "1500.50 - Simulating use")
 */
import type { Citation, CitationInput } from '__generated__/graphql';

/**
 * Form state type for citation codes using antd Select labelInValue pattern.
 * Used by CitationCodes component.
 */
export interface CitationFormValue {
  value: string;
  label?: string;
  title?: string;
  key?: string;
}

export const transformCitations = {
  /**
   * Convert schema format to form format for Select labelInValue
   * { code, description } → { value, label: "code - description", title }
   *
   * @param {Citation[]} citations - Array of citations from schema
   * @return {CitationFormValue[]} Array of labelInValue items for Select, or undefined if empty
   */
  toForm: (citations?: Citation[] | null): CitationFormValue[] | undefined => {
    if (!citations || citations.length === 0) return undefined;

    return citations.map((citation) => ({
      value: citation.code,
      label: citation.code,
      title: citation.description || citation.code,
      key: citation.code,
    }));
  },

  /**
   * Convert form format (labelInValue) to schema format
   * { value, label: "code - description" } → { code, description }
   *
   * Handles labels with " - " separator and legacy labels without separator.
   *
   * @param {CitationFormValue[]} selections - Array of labelInValue items from Select
   * @return {CitationInput[]} Array of citations for schema, or undefined if empty
   */
  toSchema: (
    selections?: CitationFormValue[] | null,
  ): CitationInput[] | undefined => {
    if (!selections || selections.length === 0) return undefined;

    return selections.map((selection) => {
      // Parse "code - description" format, handling descriptions with " - " in them
      return {
        code: selection.value,
        description: selection.title || selection.value,
      };
    });
  },
};

export default transformCitations;
